import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import cookie from 'react-cookies';
import { MARKDOWN_TO_HTML } from '../../../common/custom/string-data';
import BuildSteps from './BuildSteps';
import AttachmentPreviewModal from './AttachmentPreviewModal';

const BuildProgress = ({
  milestones,
  phase,
  userMessage,
  attachments,
  streamingMessage,
  isRunning,
}) => {
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const urlCacheRef = useRef({});
  const scrollAreaRef = useRef(null);

  const handleAttachmentClick = async (att) => {
    if (!att) return;
    setSelectedAttachment(att);
    setModalOpen(true);

    const cleanKey = (att.key || att.name || '').split('/').pop();
    if (!cleanKey) return;

    if (urlCacheRef.current[cleanKey]) {
      setPreviewUrl(urlCacheRef.current[cleanKey]);
      setPreviewLoading(false);
      setPreviewError('');
      return;
    }

    setPreviewLoading(true);
    setPreviewError('');
    setPreviewUrl('');

    try {
      const baseUrl = process.env.REACT_APP_BUILDER_ENGINE;
      const normalized = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
      const apiUrl = `${normalized}file/user-prompt-attachment-url/${encodeURIComponent(cleanKey)}`;

      const token = cookie.load('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      const response = await axios.get(apiUrl, config);
      const fileUrl = response && response.data && response.data.url;

      if (fileUrl) {
        urlCacheRef.current[cleanKey] = fileUrl;
        setPreviewUrl(fileUrl);
      } else {
        setPreviewError('Attachment URL not returned from server.');
      }
    } catch (err) {
      const errorMsg =
        (err && err.response && err.response.data && err.response.data.message) ||
        err.message ||
        'Failed to fetch attachment URL.';
      setPreviewError(errorMsg);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const chunkHtmlList = useMemo(() => {
    if (!streamingMessage) return [];
    return streamingMessage
      .split(/\n\s*\n/)
      .map((chunk) => chunk.trim())
      .filter(Boolean)
      .map((chunk) => MARKDOWN_TO_HTML(chunk));
  }, [streamingMessage]);

  useEffect(() => {
    const node = scrollAreaRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [streamingMessage]);

  const showPromptToggle = userMessage && userMessage.length > 180;

  return (
    <div
      className="vibe-build-progress"
      style={{
        height: '100%',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        ref={scrollAreaRef}
        className="vibe-build-progress-scroll-area"
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: '4px' }}
      >
        {(userMessage || (Array.isArray(attachments) && attachments.length > 0)) && (
          <div className="vibe-user-message-window mb-3">
            <div className="d-flex align-items-center mb-1">
              <span
                className="d-inline-flex align-items-center justify-content-center text-white mr-2"
                style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  background: '#303453',
                  fontSize: '11px',
                  fontWeight: 600,
                }}
                aria-hidden="true"
              >
                Y
              </span>
              <span className="text-muted font-weight-bold" style={{ fontSize: '12px' }}>
                Your request
              </span>
            </div>
            {userMessage ? (
              <div
                style={{
                  fontSize: '14px',
                  color: '#1e293b',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.5',
                  ...(!isPromptExpanded
                    ? {
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }
                    : {}),
                }}
              >
                {userMessage}
              </div>
            ) : null}
            {showPromptToggle && (
              <button
                type="button"
                className="btn btn-link p-0 mt-1"
                onClick={() => setIsPromptExpanded(!isPromptExpanded)}
                style={{
                  fontSize: '12px',
                  color: '#2563eb',
                  textDecoration: 'none',
                  fontWeight: 500,
                }}
                aria-expanded={isPromptExpanded}
              >
                {isPromptExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
            {Array.isArray(attachments) && attachments.length > 0 && (
              <div className="d-flex flex-wrap mt-2" style={{ gap: '6px' }}>
                {attachments.map((att, idx) => (
                  <span
                    key={att.key || idx}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleAttachmentClick(att)}
                    className="badge badge-light border text-dark px-2 py-1 d-inline-flex align-items-center"
                    style={{ fontSize: '11px', cursor: 'pointer', transition: 'background-color 0.15s ease' }}
                    title={`Click to preview ${att.name || att.key}`}
                  >
                    <i className="wb webconnect-paperclip mr-1" aria-hidden="true" />
                    {att.name || att.key}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {(chunkHtmlList.length > 0 || isRunning) && (
          <div className="mb-2">
            <div
              className="text-muted font-weight-bold mb-2 d-flex align-items-center"
              style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              <span>Agent response</span>
            </div>
            {chunkHtmlList.length > 0 ? (
              chunkHtmlList.map((html, idx) => (
                <div
                  key={idx}
                  className="vibe-coding-chat__message-content mb-3"
                  style={{ fontSize: '13px', lineHeight: '1.6', color: '#1e293b' }}
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              ))
            ) : (
              <span className="text-muted" style={{ fontSize: '13px' }}>
                Working on your request…
              </span>
            )}
          </div>
        )}
      </div>

      <BuildSteps milestones={milestones} phase={phase} isRunning={isRunning} />

      <AttachmentPreviewModal
        isOpen={modalOpen}
        toggle={closeModal}
        attachment={selectedAttachment}
        url={previewUrl}
        isLoading={previewLoading}
        error={previewError}
      />
    </div>
  );
};

BuildProgress.propTypes = {
  milestones: PropTypes.array,
  phase: PropTypes.object,
  userMessage: PropTypes.string,
  attachments: PropTypes.array,
  streamingMessage: PropTypes.string,
  isRunning: PropTypes.bool,
};

BuildProgress.defaultProps = {
  milestones: [],
  phase: null,
  userMessage: '',
  attachments: [],
  streamingMessage: '',
  isRunning: false,
};

export default BuildProgress;
